// data/zones/zone1.ts — THE BROKEN GATE.
// DATA ONLY. Numbers, tables, ASCII maps. No behaviour lives here — the zone's
// mechanic is content/zones/zone1.ts and the systems that read this are game/*.
//
// DESIGN §5 Zone 1: "learn the grammar — generators, keys, food, and the choice
// between thinning the horde and cutting its source."

import type { EnemyDef, GeneratorDef, ProjectileDef, RoomTemplate, ZoneConfig, ZonePalette } from '../../types'

// ---------------------------------------------------------------- palette
// Torch-lit ruined fortress: cool grey flagstone, warm torchlight against it.

export const ZONE1_PALETTE: ZonePalette = {
  floor: '#31343d',
  floorAlt: '#3a3e49',
  wall: '#626b7b',
  wallEdge: '#8d97a8',
  accent: '#c9a227',
  glow: '#ffb347',
  bg: '#0d0f15',
  torches: 0.14,
}

// ---------------------------------------------------------------- projectiles

export const ZONE1_PROJECTILES: ProjectileDef[] = [
  // Readability law: lobbed / AoE reads green.
  {
    id: 'bonebolt',
    kind: 'lobbed',
    speed: 250,
    damage: 8,
    radius: 6,
    color: '#9be36b',
    lifetime: 3,
    arcHeight: 46,
  },
]

// ---------------------------------------------------------------- enemies

export const ZONE1_ENEMIES: EnemyDef[] = [
  {
    id: 'gnawling',
    name: 'Gnawling',
    role: 'swarm',
    hp: 14,
    speed: 118,
    radius: 9,
    touchDamage: 6,
    ai: { lungeRange: 96, lungeCooldown: 2.0, windup: 0.28, lungeSpeed: 370, lungeTime: 0.24 },
    sprite: { key: 'gnawling', size: 24, params: { color: '#6a5540', eye: '#ffb347' } },
    emberValue: 1,
    goldChance: 0.12,
  },
  {
    id: 'husk_soldier',
    name: 'Husk Soldier',
    role: 'grunt',
    hp: 34,
    speed: 62,
    radius: 11,
    touchDamage: 8,
    ai: {
      attackRange: 26,
      attackCooldown: 1.8,
      windup: 0.45,
      strikeArc: 1.5,
      strikeRange: 44,
      strikeDamage: 1.35,
    },
    sprite: { key: 'husk', size: 30, params: { color: '#5b6472', cloth: '#4a3a2e', eye: '#ffb347' } },
    emberValue: 2,
    goldChance: 0.24,
  },
  {
    id: 'bonecaster',
    name: 'Bonecaster',
    role: 'ranged',
    hp: 22,
    speed: 68,
    radius: 10,
    touchDamage: 5,
    ai: {
      keepRange: 215,
      minRange: 135,
      fireRange: 340,
      fireCooldown: 2.6,
      windup: 0.55,
      projectile: 'bonebolt',
    },
    sprite: { key: 'bonecaster', size: 30, params: { color: '#4b3f63', glow: '#9be36b' } },
    emberValue: 3,
    goldChance: 0.3,
  },
  {
    // The Gloom's own body. Fast, fragile, one damage — pressure, not threat.
    id: 'gloomspawn',
    name: 'Gloomspawn',
    role: 'swarm',
    hp: 6,
    speed: 168,
    radius: 7,
    touchDamage: 1,
    ai: { lungeRange: 74, lungeCooldown: 1.3, windup: 0.16, lungeSpeed: 300, lungeTime: 0.18 },
    sprite: { key: 'gloomspawn', size: 20, params: { color: '#2a1f3d', eye: '#c07bff' } },
    // emberValue MUST stay 0: the Gloom's punishment spawns can never be a
    // farm — an invuln bot idling at 100% gloom once banked 1000+ embers (D-012).
    emberValue: 0,
    goldChance: 0,
  },
]

// ---------------------------------------------------------------- generators

export const ZONE1_GENERATORS: GeneratorDef[] = [
  {
    id: 'gen_bonepile',
    name: 'Bone Pile',
    hp: 58,
    radius: 15,
    spawns: 'gnawling',
    interval: 3.4,
    burst: 2,
    cap: 6,
    sprite: { key: 'gen_bonepile', size: 38, params: { color: '#cfc7ad' } },
    emberValue: 4,
  },
  {
    id: 'gen_barracks',
    name: 'Broken Barracks Door',
    hp: 92,
    radius: 16,
    spawns: 'husk_soldier',
    interval: 5.2,
    burst: 1,
    cap: 4,
    sprite: { key: 'gen_barracks', size: 42, params: { color: '#5a4126' } },
    emberValue: 5,
  },
  {
    id: 'gen_brazier',
    name: 'Altar-Brazier',
    hp: 70,
    radius: 14,
    spawns: 'bonecaster',
    interval: 6.4,
    burst: 1,
    cap: 3,
    sprite: { key: 'gen_brazier', size: 38, params: { flame: '#9be36b' } },
    emberValue: 5,
  },
]

// ---------------------------------------------------------------- rooms
// 44x26 tiles at 32px. Eight combat templates in three ordered difficulty
// groups — the zone runner splits the combat library into roomCount contiguous
// groups and draws one from each, so a run always reads:
// one generator -> two -> three-and-a-gate.
//
//   group 1 (1 generator):  z1-gatehouse, z1-longhall
//   group 2 (2 generators): z1-twinyards, z1-pillarcourt, z1-cistern*
//   group 3 (3 generators): z1-barredvault, z1-warrens, z1-strongroom*
//   (* carries the timed vault — 'V' door into a 'v' alcove)
//
// The respite (z1-respite) lives in the same array but is never a combat pick:
// the runner recognises it by its altar glyph.
//
// Glyph legend is types.ts; this zone adds four respite/vault glyphs documented
// in game/rooms.ts: 'A' altar, 'N' fountain, 'S' boss door, 'v' vault interior.
// One character per tile.

export const ZONE1_ROOMS: RoomTemplate[] = [
  // The Gatehouse — room 1 shape: one generator, in plain sight. Kill it, feel the flow stop.
  {
    id: 'z1-gatehouse',
    generators: ['gen_bonepile'],
    patrols: ['gnawling', 'husk_soldier'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#...@.......................$..............#',
      '#...........E..............................#',
      '#.....####........................####.....#',
      '#.....####........................####.....#',
      '#.....####............G...........####.....#',
      '#.....####........................####.....#',
      '#.......................#..................#',
      '#.......................#..................#',
      '#.......................#..................#',
      '#.............#######...#######............#',
      '#...................#...............F......#',
      '#.......F...........#......................#',
      '#...................#......................#',
      '#..........................................#',
      '#.....####........................####.....#',
      '#.....####........................####.....#',
      '#.....####.......$................####.....#',
      '#.....####........................####.....#',
      '#...............................E..........#',
      '#.....................F....................#',
      '#..........................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Long Hall — room 1 shape: one generator at the far end of a colonnade. Walk into the pour.
  {
    id: 'z1-longhall',
    generators: ['gen_barracks'],
    patrols: ['husk_soldier', 'gnawling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#.............................F............#',
      '#..............E...........................#',
      '#...................................C......#',
      '#.......##....##....##....##....##.........#',
      '#.......##....##....##....##....##.........#',
      '#.......##....##....##....##....##.........#',
      '#.......................................G..#',
      '#..........................................#',
      '#..........................................#',
      '#############.....#########.....#########..D',
      '#..........................................#',
      '#..........................................#',
      '#..............$...........................#',
      '#.......##....##....##....##....##.........#',
      '#.......##....##....##....##....##.........#',
      '#.......##....##....##....##....##.........#',
      '#..........................................#',
      '#.....................................F....#',
      '#..@..F.............E......E...............#',
      '#..........................................#',
      '#..........................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // Twin Yards — room 2 shape: two generators, far apart, split by a spine. You cannot watch both.
  {
    id: 'z1-twinyards',
    generators: ['gen_bonepile', 'gen_barracks'],
    patrols: ['gnawling', 'husk_soldier', 'bonecaster'],
    map: [
      '#################################D##########',
      '#.....................#....................#',
      '#.....................#....................#',
      '#.....G...............#.................F..#',
      '#.....................#....................#',
      '#...###########.......#...........E........#',
      '#.....................#....................#',
      '#.....................#....................#',
      '#.......####.....F....#....................#',
      '#.......####..........#....................#',
      '#.......####...............................#',
      '#...................#####..................#',
      '#...$.........E.....##.##.....E........$...#',
      '#...................#####..................#',
      '#...................#####..................#',
      '#...............................####.......#',
      '#.....................#.........####.......#',
      '#.....................#.........####.......#',
      '#.....................#....F...............#',
      '#.....................#....................#',
      '#........E............#......###########...#',
      '#.....................#....................#',
      '#.....................#...............G....#',
      '#..@..................#....................#',
      '#.....................#....................#',
      '############D###############################',
    ],
  },
  // The Pillar Court — room 2 shape: two generators diagonally opposed in cover. Sightlines are the choice.
  {
    id: 'z1-pillarcourt',
    generators: ['gen_barracks', 'gen_brazier'],
    patrols: ['husk_soldier', 'bonecaster', 'gnawling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#...G................E.....................#',
      '#....##....##....##....##....##....##...F..#',
      '#....##....##....##....##....##....##......#',
      '#..........................................#',
      '#..........................................#',
      '#...........E..............................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#.........................F................#',
      'D..........................................D',
      '#...........#########..#########...........#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#.............$............................#',
      '#..............................E...........#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..F.......................................#',
      '#....................@.................G...#',
      '#..........................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // The Cistern — room 2 shape, and the zone's timed vault: two generators, and a
  // golden door in the north wall that only answers a Warden who arrived early.
  {
    id: 'z1-cistern',
    generators: ['gen_bonepile', 'gen_brazier'],
    patrols: ['gnawling', 'husk_soldier', 'bonecaster'],
    map: [
      '############################################',
      '#...........................#vvvvvvvv#.....#',
      '#...........................Vvvvvvvvv#.....#',
      '#...........................Vvvvvvvvv#.....#',
      '#....@......................#vvvvvvvv#.....#',
      '#...........................##########.....#',
      '#.....####.........................E.......#',
      '#.....####........F........................#',
      '#.....####.................................#',
      '#..........................................#',
      '#...........G..............................#',
      '#..........................................#',
      '#.......########..............########.....#',
      'D..........................................#',
      '#..........................................#',
      '#..............E...........................#',
      '#..........................$..............F#',
      '#..........................................#',
      '#....####..................................#',
      '#....####...........E......................#',
      '#....####..................................#',
      '#..........................................#',
      '#..................................G.......#',
      '#.......F..................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // The Barred Vault — room 3 shape: three generators; the third is behind an iron gate and the key walks the room.
  {
    id: 'z1-barredvault',
    generators: ['gen_bonepile', 'gen_barracks', 'gen_brazier'],
    patrols: ['husk_soldier', 'gnawling', 'bonecaster'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#..@..........F.........E..................#',
      '#...........................################',
      '#...........................#..............#',
      '#.......###.................#..............#',
      '#.......###.................#.......G......#',
      '#.......###.........K.......LLL............#',
      '#...........................#..............#',
      '#................####.......#....$.....C...#',
      '#................####.......#..............#',
      '#.....E..........####.......################',
      '#................####......................#',
      '#...........G..............................#',
      '#........................E.................#',
      '#...F...........#..........................#',
      '#.......###.....#..........................#',
      '#.......###.....#..........................#',
      '#.......###.....#..........................#',
      '#..##############.............F............#',
      '#.....................G....................#',
      '#.........................F................#',
      '#.......E..................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Warrens — room 3 shape: three generators knotted into corridors; the gated alcove holds the last.
  {
    id: 'z1-warrens',
    generators: ['gen_bonepile', 'gen_brazier', 'gen_barracks'],
    patrols: ['husk_soldier', 'bonecaster', 'gnawling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#..@......G....................E........F..#',
      '#..........................................#',
      '#..........................................#',
      '###############.....#############...#......#',
      '#.............#.....................#......#',
      '#.####........#.....................#......#',
      '#.####........#.....E...............#..E...#',
      '#.####........#.....................#......#',
      '#.............#.....................#......#',
      '#...$......P..#..........K..#.......#......#',
      '#...........................#..............#',
      '#...........................#..............#',
      'D....E......................#....G.........D',
      '#...........................#..............#',
      '#...........................#..............#',
      '#.......#.#############.....################',
      '#.......#..................................#',
      '#.......#.............................####.#',
      '#.F.....#...F.....#LLLLL#.............####.#',
      '#.......#.........#.....#.........F...####.#',
      '#.......#.........#..G$.#..................#',
      '#.......#.........#.....#..................#',
      '############################################',
    ],
  },
  // The Strongroom — room 3 shape, and the zone's other timed vault: three
  // generators spread wide, with the golden door tucked in the south-west so the
  // fast route and the safe route are not the same route.
  {
    id: 'z1-strongroom',
    generators: ['gen_barracks', 'gen_bonepile', 'gen_brazier'],
    patrols: ['husk_soldier', 'bonecaster', 'gnawling'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@...........E...........................#',
      '#..........................................#',
      '#........####..................####........#',
      '#........####......G...........####........#',
      '#........####..................####........#',
      '#..........................................#',
      '#.................F........................#',
      '#..........................................#',
      'D.........................E................#',
      '#..........................................#',
      '#.....############......############.......#',
      '#..........................................#',
      '#...........G.....................$........#',
      '#..........................................#',
      '#..........................E...............#',
      '#..........................................#',
      '#....########..............................#',
      '#....#vvvvvv#..........F...................#',
      '#....#vvvvvvV..............................#',
      '#....#vvvvvvV.................G............#',
      '#....########..............................#',
      '#..........................................#',
      '#.......F..................................#',
      '############################################',
    ],
  },
  // ------------------------------------------------------------- the respite
  // Not a combat room and never drawn as one: the runner finds it by the altar
  // glyph. A cold chapel above the stair — Altar ('A'), Fountain ('N'), and the
  // sealed boss door ('S') in the north wall. Nothing spawns here; the Gloom
  // stops filling (DESIGN §3.1). Both fixtures stand free in the open so the
  // Warden can reach them from any side.
  {
    id: 'z1-respite',
    generators: [],
    patrols: [],
    map: [
      '#################S################',
      '#................................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#.......A.................N......#',
      '#................................#',
      '#................................#',
      '#....##..................##......#',
      '#....##..................##......#',
      '#................................#',
      '#..............@.................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '##################################',
    ],
  },
]

// ============================================================================
// BOSS 1 — THE BROODGATE (DESIGN §5 Zone 1)
// "the boss is a generator network." Every number the fight reads lives here.
// content/bosses/boss1.ts is the behaviour and holds no literals.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// A wide hall, 44x26 like the zone's combat rooms so the camera reads the same.
// The Broodgate is fused into the north wall; two pillars stand mid-hall and are
// the only cover from the sweeping beam (the beam is a ray from the Heart, so a
// pillar casts a real shadow). No generators and no patrols: every body in this
// room comes out of a maw. 'D' in the south wall is the descent, sealed until
// the Heart stops.
export const ZONE1_BOSS_ARENA: RoomTemplate = {
  id: 'z1-broodgate',
  generators: [],
  patrols: [],
  map: [
    '############################################',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.............##............##.............#',
    '#.............##............##.............#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.....F..............................F.....#',
    '#..........................................#',
    '#.....................@....................#',
    '#..........................................#',
    '#..........................................#',
    '######################D#####################',
  ],
}

// ------------------------------------------------------- boss parts as data
// The maws are ordinary destructible generators so that every weapon, splash,
// burn trail and potion already knows how to chew them — "the boss is a
// generator network" is literal. They pour nothing on their own (cap 0); the
// Broodgate owns pour timing and composition per phase.
//
// emberValue MUST stay 0: a maw regrows, and a regrowing ember source is a farm
// (D-012, the gloomspawn lesson). The fight pays out once, at the kill.
export const ZONE1_BOSS_GENERATORS: GeneratorDef[] = [
  {
    id: 'broodgate_maw',
    name: 'Spawn-Maw',
    hp: 130,
    radius: 17,
    spawns: 'gnawling',
    interval: 4.6,
    burst: 0,
    cap: 0,
    sprite: { key: 'broodgate_maw', size: 40, params: { bone: '#d8cfb4', maw: '#2b1a24' } },
    emberValue: 0,
  },
]

export const ZONE1_BOSS_PROJECTILES: ProjectileDef[] = [
  // Readability law: straight = white-gold. The Heart's spread is bone-gold and
  // carries its own body so it is never mistaken for the Warden's arrows.
  {
    id: 'broodbolt',
    kind: 'straight',
    speed: 300,
    damage: 9,
    radius: 6,
    color: '#ffd98a',
    lifetime: 3.2,
  },
]

/**
 * Boss tuning block. Arrays indexed by phase-1 (P1, P2, P3).
 *
 * FLAGGED INTERPRETATION (`exposeAt`): DESIGN §5 states the rule as "the Heart
 * is only vulnerable while >=2 maws are down" and then says of P3 "only 2 maws
 * must be down". Taken literally the threshold is 2 throughout, which is what
 * ships here; P3's real escalation is the doubled pour and the two-way beam.
 * If the intent was 3/3/2, this one array is the whole change.
 */
export const ZONE1_BOSS = {
  id: 'broodgate',
  name: 'THE BROODGATE',

  /** Tile coordinates inside ZONE1_BOSS_ARENA. */
  heartTile: { tx: 22, ty: 2 },
  heartHp: 620,
  heartRadius: 34,
  /** Staggered heights along the far wall — no two maws sit on the same line. */
  mawTiles: [
    { tx: 9, ty: 2 },
    { tx: 17, ty: 4 },
    { tx: 31, ty: 3 },
  ],

  /** Heart HP fractions at which P2 and P3 begin. */
  phase2At: 0.6,
  phase3At: 0.25,

  /** Maws that must be down before the Heart can be hurt, per phase. */
  exposeAt: [2, 2, 2],
  /** Seconds a destroyed maw takes to come back, per phase. */
  regrowSeconds: [20, 14, 14],
  /** Added to a regrow timer for each maw already regrowing — they never return together. */
  regrowStagger: 3.5,

  /** Seconds between pours, per phase. */
  pourInterval: [4.6, 3.8, 3.2],
  /** Bodies per pour per living maw, per phase. */
  pourCount: [1, 1, 2],
  /** Pour table per phase, cycled per maw. P2 is where Bonecasters join. */
  pourTable: [
    ['gnawling', 'gnawling', 'husk_soldier'],
    ['gnawling', 'husk_soldier', 'bonecaster'],
    ['gnawling', 'husk_soldier', 'bonecaster'],
  ] as string[][],
  /** Hard ceiling on live pour-children, so the hall never becomes soup. */
  pourLiveCap: 22,

  /** The Heart's 3-bolt spread. */
  bolt: {
    id: 'broodbolt',
    count: 3,
    spreadDeg: 26,
    windup: 0.6,
    interval: [3.4, 2.8, 2.4],
  },

  /** The sweeping beam (P2 onward). A ray from the Heart; pillars shadow it. */
  beam: {
    /** windup >= 0.4s is the readability law's floor for a new tell. */
    windup: 1.0,
    sweepSeconds: 2.0,
    damage: 16,
    /** half-width of the lane in px */
    laneHalf: 26,
    fromDeg: 156,
    toDeg: 24,
    /** Seconds between beams, per phase. 0 = the phase does not use it. */
    interval: [0, 8, 6.5],
  },

  /** Kill moment. */
  death: { shake: 13, shakeSeconds: 1.1, hitstop: 0.3 },
} as const

// ---------------------------------------------------------------- zone

export const ZONE1: ZoneConfig = {
  id: 1,
  name: 'The Broken Gate',
  tagline: 'Cut the source, or drown in what it pours.',
  palette: ZONE1_PALETTE,
  rooms: ZONE1_ROOMS,
  roomCount: 3,
  gloomFillSeconds: 90,
  vaultClockSeconds: 170,
  eliteRate: 0.08,
  eliteRiders: ['frenzied', 'bloated'],
  statScale: { hp: 1, damage: 1 },
  foodPerRoom: [2, 3],
  music: {
    root: 45,
    mode: 'aeolian',
    tempo: 96,
    padLevel: 0.9,
    bassLevel: 0.85,
    percLevel: 0.9,
    character: 'drips',
  },
  bossId: 'broodgate',
}
